import React, { useState, useEffect } from "react";
import uuid from "uuid";
import axios from "axios";
import { useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Fab from "@material-ui/core/Fab";
import CameraIcon from "@material-ui/icons/PhotoCamera";

import { getExtension } from "../../utils/file";
// import { MEDIA_S3_PRESIGNED_PUT_URL, CACHE_CONTROL } from "../../config";
import { MEDIA_GET_UPLOAD_URL } from "../../config";
import SubmitButton from "../button/SubmitButton";
import CancelButton from "../button/CancelButton";
import UploadPreviewGrid from "./UploadPreviewGrid";
import { showErrorAlert } from "../../store/actions/alerts";

import makeApiRequest from "../../utils/axios/new-axios";

const useStyles = makeStyles((theme) => ({
  input: {
    display: "none",
  },
}));

export default function UploadMediaModal({
  openModal,
  handleCloseModal,
  // bucket,
  isPublic,
  setUploadMedias,
  stopSubmitting,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [mediasArray, setMediasArray] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [hasMedia, setHasMedia] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function onUploadClickHandler() {
    setIsSubmitting(true);

    const signedUrlReq = [];
    mediasArray.forEach((media) => {
      signedUrlReq.push({
        // bucket: bucket, // set from server
        key: media.key,
        metaData: {
          contentType: media.type,
          // cacheControl: CACHE_CONTROL, // set from server
        },
        isPublic: 0, // media.isPublic -- defaulted to make the files private in s3
      });
    });

    if (signedUrlReq.length > 0) {

      //get presigned url
      makeApiRequest({
        url: MEDIA_GET_UPLOAD_URL.url,
        method: MEDIA_GET_UPLOAD_URL.method,
        data: signedUrlReq,
      })
        .then((response) => {
          for (let i = 0; i < mediasArray.length; i++) {
            const media = mediasArray[i];
            const uploadItem = response.data.find((item) => {
              return item.key === media.key;
            });

            mediasArray[i].bucket = uploadItem.bucket;
            mediasArray[i].type = uploadItem.contentType;

            axios({
              method: "put",
              url: uploadItem.presignedUrl,
              data: media.file,
              headers: {
                "Content-Type": uploadItem.contentType,
                "Cache-Control": uploadItem.cacheControl,
              },
              onUploadProgress: (e) => {
                var percentCompleted = Math.round((e.loaded * 100) / e.total);
                updateMediaUploadProgress(media.key, percentCompleted);
              },
            })
              .then((response) => {
                updateMediaUploadStatus(
                  media.key,
                  response.status === 200 ? 1 : 6
                ); //upload success (1) or something went wrong (6 status for error)
              })
              .catch(() => {
                updateMediaUploadStatus(media.key, 6); //status for error uploading
              });
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          dispatch(
            showErrorAlert(
              "Error : " +
              (!!error.response
                ? `${error.response.status}! - ${error.response.data.errorMessage}`
                : error.message)
            )
          );
        });
    }
  }

  function onInputFileChangeHandler({ target }) {
    let fileArray = [];
    for (let i = 0; i < target.files.length; i++) {
      const key = uuid.v4();
      const file = target.files[i];
      const name = file.name;
      const ext = getExtension(name).toLowerCase();
      const fileObject = {
        file: file,
        name: name,
        ext: ext,
        size: file.size,
        type: file.type,
        key: `${key}.${ext}`,
        isPublic: isPublic,
        url: URL.createObjectURL(file),
        errors: 0,
        uploadStatus: 0,
        uploadProgress: 0,
      };
      fileArray.push(fileObject);
    }
    setMediasArray([...mediasArray, ...fileArray]);
  }

  useEffect(() => {
    stopSubmitting && setIsSubmitting(false);
  }, [stopSubmitting]);

  useEffect(() => {
    let foundError = false;
    let uploadSuccessCount = 0;
    let uploadErrorCount = 0;

    for (let i = 0; i < mediasArray.length; i++) {
      //check for any errors in the media details
      if (mediasArray[i].errors > 0) {
        foundError = true;
        break;
      }
      //get the number of success uploads
      if (mediasArray[i].uploadStatus === 1) {
        uploadSuccessCount++;
      }
      //get the number of error uploads
      if (mediasArray[i].uploadStatus === -1) {
        uploadErrorCount++;
      }
    }
    setHasError(foundError);
    setHasMedia(mediasArray.length > 0 ? true : false);

    //check if the upload process has been completed
    const uploadCount = uploadSuccessCount + uploadErrorCount;
    if (!foundError && uploadCount > 0 && uploadCount === mediasArray.length) {
      setUploadMedias(mediasArray);
    }
  }, [mediasArray, setUploadMedias]);

  //reset media array upon exiting the modal
  useEffect(() => {
    if (!openModal) {
      mediasArray.forEach((media) => {
        URL.revokeObjectURL(media.url);
      });
      setMediasArray([]);
    }
    // eslint-disable-next-line
  }, [openModal]);

  function removeMedia(key, url) {
    URL.revokeObjectURL(url);
    setMediasArray(
      mediasArray.filter((media) => {
        return media.key !== key;
      })
    );
  }

  function updateMediaData(key, value) {
    setMediasArray(
      mediasArray.filter((media) => {
        return media.key === key ? (media.details = { ...value }) : media;
      })
    );
  }

  function updateMediaUploadStatus(key, value) {
    setMediasArray(
      mediasArray.filter((media) => {
        return media.key === key ? (media.uploadStatus = value) : media;
      })
    );
  }

  function updateMediaUploadProgress(key, value) {
    setMediasArray(
      mediasArray.filter((media) => {
        return media.key === key ? (media.uploadProgress = value) : media;
      })
    );
  }

  function setErrors(key, count) {
    setMediasArray(
      mediasArray.filter((media) => {
        return media.key === key ? (media.errors = count) : media;
      })
    );
  }

  return (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      fullWidth={true}
      maxWidth="md"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
    >
      <DialogTitle id="alert-dialog-title">
        Upload Media
        <input
          className={classes.input}
          id="upload-img-button"
          type="file"
          accept="image/*"
          name="file"
          onChange={onInputFileChangeHandler}
          disabled={isSubmitting}
          multiple
        />
        <Box justifyContent="center" display="flex">
          <label htmlFor="upload-img-button">
            <Fab
              size="large"
              color="primary"
              aria-label="add"
              component="span"
              disabled={isSubmitting}
            >
              <CameraIcon />
            </Fab>
          </label>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {mediasArray.map((media) => {
          return (
            <UploadPreviewGrid
              media={media}
              key={media.key}
              removeMedia={removeMedia}
              updateMediaData={updateMediaData}
              setErrors={setErrors}
              isSubmitting={isSubmitting}
              uploadProgress={media.uploadProgress}
            />
          );
        })}
        <br />
      </DialogContent>
      <DialogActions style={{ margin: "20px" }}>
        <CancelButton onClick={handleCloseModal} disabled={isSubmitting}>
          Cancel
        </CancelButton>
        <SubmitButton
          onClick={onUploadClickHandler}
          disabled={hasError || !hasMedia || isSubmitting}
          isSubmitting={isSubmitting}
        >
          Upload
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
